import { MapContainer, TileLayer, ZoomControl } from "react-leaflet";
import { useQuery } from "@tanstack/react-query";
import type { VillageViewModel } from "../../domain/contracts";
import { MapClickReset, MapCenterUpdater, MapVillageOverlay } from "./lib/mapUtils";
import { useMapInteraction } from "./hooks/useMapInteraction";
import { VillageSearchBar } from "./lib/VillageSearchBar";
import { villageTagsQueryOptions } from "../../services/queries/publicQueryOptions";

type MapSectionProps = {
  villages: VillageViewModel[];
  previewVillages: VillageViewModel[];
  selectedVillage: VillageViewModel | null;
  onSelectVillage: (villageRef: string | null) => void;
  onViewFullProfile: (villageRef: string) => void;
};

export function MapSection({ villages, selectedVillage, onSelectVillage, onViewFullProfile }: MapSectionProps) {
  const {
    panTarget,
    setPanTarget,
    zoomTarget,
    setZoomTarget,
    activeVillage,
    center,
    hasMappedPreviewVillages,
    isPinnedSelection,
    handleMarkerHover,
    handleMarkerLeave,
    handleMarkerClick,
    clearPinnedVillage,
  } = useMapInteraction(villages, selectedVillage);

  const status2019Label = activeVillage?.reference?.status2019 ?? "-";

  const tagsQuery = useQuery(villageTagsQueryOptions(
    isPinnedSelection ? (selectedVillage ?? activeVillage)?.backendRef ?? null : null
  ));
  const tags = tagsQuery.data?.tags ?? [];

  function handleClearAll() {
    clearPinnedVillage();
    onSelectVillage(null);
  }

  function handleViewFullProfile() {
    if (!activeVillage) return;
    onViewFullProfile(activeVillage.id);
  }

  return (
    <section id="map" className="reference-section reference-section-map" aria-labelledby="map-layout-title">
      <div className="map-section__intro">
        <h1 id="map-layout-title" className="section-title fade-in">Mapping the Archive</h1>
        <p className="map-section__description fade-in fade-in--delay-1">
          This map indicates locations and details of native Rohingya villages that have been ethnically cleansed. Each coordinate maps to a village's documented location.
        </p>
      </div>

      <div className="frame frame-map">
        <div className="map-shell">
          <VillageSearchBar villages={villages} onSelectVillage={onSelectVillage} setPanTarget={setPanTarget} setZoomTarget={setZoomTarget} />

          <div className="map-legend" aria-label="Map marker legend">
            {hasMappedPreviewVillages ? (
              <>
                <span className="map-legend__item"><img src="/village-icon.png" alt="" className="map-legend__icon" aria-hidden="true" /> Has victim data</span>
                <span className="map-legend__item"><img src="/village-icon-no-data.png" alt="" className="map-legend__icon" aria-hidden="true" /> No victim data available</span>
              </>
            ) : (
              <p className="map-legend__warning">No preview-backed villages currently have usable matched coordinates. The map is showing reference geography only.</p>
            )}
          </div>
          <MapContainer center={center} zoom={9} scrollWheelZoom zoomControl={false} className="map-canvas fade-in fade-in--delay-2">
            <MapClickReset onClear={handleClearAll} />
            <MapCenterUpdater panTarget={panTarget} zoom={zoomTarget} />
            <MapVillageOverlay
              villages={villages}
              onMarkerClick={handleMarkerClick}
              onMarkerHover={handleMarkerHover}
              onMarkerLeave={handleMarkerLeave}
            />
            <ZoomControl position="bottomright" />
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
          </MapContainer>

          <aside className={`map-profile-card${activeVillage ? " is-visible" : " is-hidden"}${isPinnedSelection ? " is-pinned" : ""}`}>
            {activeVillage ? (
              <>
                <div className="map-profile-head">
                  <h2>{activeVillage.title}</h2>
                  <button type="button" className="map-profile-close" aria-label="Close" onClick={handleClearAll}>×</button>
                </div>

                <div className="map-profile-block">
                  {/* <p className="map-label">Village Name: <strong>{activeVillage.title}</strong></p> */}
                  {activeVillage.reference?.myanmarName && (
                    <p className="map-label">Myanmar Name: <strong>{activeVillage.reference.myanmarName}</strong></p>
                  )}
                  <p className="map-label">Township: <strong>{activeVillage.township}</strong></p>
                </div>

                {activeVillage.backendRef ? (
                  <>
                    <div className="map-profile-block">
                      <h3>Summary</h3>
                      <dl className="stat-list">
                        <div><dt>Status 2019:</dt><dd>{status2019Label}</dd></div>
                        <div><dt>Survivor Accounts:</dt><dd>{activeVillage.stats.survivorAccountCount}</dd></div>
                        <div><dt>Evidence Items:</dt><dd>{activeVillage.stats.evidenceItemCount}</dd></div>
                      </dl>
                    </div>

                    {tags.length > 0 && (
                      <div className="map-profile-block">
                        <h3>Reported Human Rights Violations</h3>
                        <dl className="stat-list">
                          {tags.map((item) => (
                            <div key={item.tag}><dt>{item.label}:</dt><dd>{item.count}</dd></div>
                          ))}
                        </dl>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="map-profile-block">
                    <p className="micro-copy">No survivor data has been collected for this village.</p>
                    <p className="micro-copy">
                      {activeVillage.coordinates.lat !== 0 || activeVillage.coordinates.lng !== 0
                        ? `${activeVillage.coordinates.lat.toFixed(6)}, ${activeVillage.coordinates.lng.toFixed(6)}`
                        : "Coordinates unavailable for this reference point."}
                    </p>
                  </div>
                )}

                <div className="map-profile-block">
                  <div className="action-stack">
                    <button type="button" className="action-button action-button-gold" onClick={handleViewFullProfile}>View Full Profile</button>
                  </div>
                </div>
              </>
            ) : null}
          </aside>
        </div>
      </div>
    </section>
  );
}